import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { MobileSignRequestStyles } from "../MobileSignRequest.styles";
import { pressableProps } from "../mobileA11y";

export interface MobileStep {
  label: string;
  isActive: boolean;
  isCompleted: boolean;
}

interface MobileStepperProps {
  steps: MobileStep[];
  onStepClick?: (index: number) => void;
}

/**
 * Presentational 6-pill stepper for the mobile sign-request flow
 * (Upload → Signers → Fields → Mode → Preferences → Send).
 */
const MobileStepper: React.FC<MobileStepperProps> = ({ steps, onStepClick }) => {
  const theme = useTheme();
  const classes = MobileSignRequestStyles(theme);

  return (
    <Box component="nav" aria-label="Progress" sx={classes.stepperRow}>
      {steps.map((step, index) => {
        const prev = steps[index - 1];
        const status = step.isActive
          ? "current step"
          : step.isCompleted
            ? "completed"
            : "not completed";
        const accessibleName = `Step ${index + 1} of ${steps.length}: ${step.label}, ${status}`;
        return (
          <Box
            key={step.label}
            sx={classes.stepItem}
            {...(onStepClick
              ? pressableProps(() => onStepClick(index), {
                  label: accessibleName,
                  current: step.isActive ? "step" : undefined,
                })
              : {
                  "aria-current": step.isActive ? "step" : undefined,
                  "aria-label": accessibleName,
                })}
          >
            {index > 0 && (
              <Box
                aria-hidden="true"
                sx={classes.stepConnector(!!prev?.isActive || !!prev?.isCompleted)}
              />
            )}
            <Box
              aria-hidden="true"
              sx={classes.stepCircle(step.isActive, step.isCompleted)}
            >
              {index + 1}
            </Box>
            <Typography aria-hidden="true" sx={classes.stepLabel(step.isActive)}>
              {step.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default MobileStepper;
